import { Address } from '@adornis/address/db/address.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import '@adornis/internationalization/client/language-picker/x-country-picker.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { of, switchMap } from 'rxjs';
import { Location } from '../db/location.js';

const locationSelector = {
  _id: 1,
  displayName: 1,
  address: {
    name: 1,
    streetAndHousenumber: 1,
    zipCity: { zip: 1, city: 1 },
    bundesland: 1,
    country: 1,
    additionalInfo: 1,
  },
} as const satisfies BaseQLSelectionSet<Location>;

/**
 * @element ils-location-edit
 */
@customElement('ils-location-edit')
export class ILSLocationEdit extends XDialog<void> {
  static override get element_name() {
    return 'ils-location-edit';
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': { display: 'block', maxWidth: '60vw', width: '60vw' },
      },
    ];
  }

  @property({ attribute: false }) locationID = new RXController(this, undefined as Maybe<string>);

  @state() location = new RXController(
    this,
    this.locationID.observable.pipe(
      switchMap(locationID => {
        if (!locationID) return of(new Location({ address: new Address({ country: 'DE' }) }));
        return getByID(Location, locationID)(locationSelector);
      }),
    ),
  );

  @state() locationSaving = false;

  @state() private readonly _form = new FormController(this, this.location.observable, {
    success: async location => {
      this.locationSaving = true;

      try {
        this.locationID.value ? await location.save() : await location.create();
        void XSnackbar.show(`Standort ${location.displayName} gespeichert!`);
        void this.close();
      } catch (err) {
        logger.error({ err }, 'Fehler beim Speichern des Standorts');
        void XDialog.alert('Fehler beim Speichern des Standorts');
      } finally {
        this.locationSaving = false;
      }
    },
    error: error => {
      this.locationSaving = false;
      logger.error(error);
      void XDialog.alert(error.message);
    },
  });

  override render() {
    return html`
      <x-flex padding="md" space="sm">
        <x-h2> Standort ${this.locationID.value ? 'bearbeiten' : 'anlegen'} </x-h2>
        ${permissionGuard({
          resource: 'locations',
          action: this.locationID.value ? 'modify' : 'create',
          guardedTemplate: this.locationForm(),
          forbiddenTemplate: html`
            <x-text> Keine Berechtigung Standort ${this.locationID.value ? 'zu bearbeiten' : 'anzulegen'} </x-text>
          `,
        })}
        ${this._buttons()}
      </x-flex>
    `;
  }

  private locationForm() {
    if (!this.location.value) return html`<x-loading-state></x-loading-state>`;

    return html`
      <x-form .controller=${this._form}>
        <x-flex space="md">
          <x-input label="Name" ${this._form.field('displayName')} placeholder="Anzeigename"></x-input>

          <x-h3>Adresse</x-h3>
          ${this._addressInputs()}
        </x-flex>
      </x-form>
    `;
  }

  private _addressInputs() {
    if (!this.location.value || !this.location.value.address) return nothing;

    return html`
      <x-input ${this._form.field('address', 'name')} placeholder="Name/Bezeichnung"></x-input>
      <x-input ${this._form.field('address', 'streetAndHousenumber')} placeholder="Straße und Hausnummer"></x-input>
      <x-flex horizontal space="md" crossaxis-end>
        <x-input
          ${this._form.field('address', 'zipCity', 'zip')}
          placeholder="PLZ"
          ${css({ maxWidth: '30%' })}
        ></x-input>
        <x-input flex ${this._form.field('address', 'zipCity', 'city')} placeholder="Stadt"></x-input>
      </x-flex>
      <x-input ${this._form.field('address', 'bundesland')} placeholder="Bundesland"></x-input>
      <x-country-picker
        language="de"
        placeholder="Land"
        ${this._form.field('address', 'country')}
        empty-state="Geben Sie den Namen des Landes ein"
      ></x-country-picker>
      <x-input ${this._form.field('address', 'additionalInfo')} placeholder="Zusätzliche Informationen"></x-input>
    `;
  }

  private _buttons() {
    return html`
      <x-flex horizontal end space="sm">
        <x-button mode="outline" @click=${() => this.cancel()}> Abbrechen </x-button>
        <x-button ?loading=${this.locationSaving} @click=${() => this._form.validate()}> Speichern </x-button>
      </x-flex>
    `;
  }

  override cancel(returnVal?: any): Promise<void> {
    this._form.document?.revert();
    return super.cancel(returnVal);
  }
}
